// Kode error sisi customer. Tiap aksi yang gagal dapat KODE PENDEK yang ikut tampil di toast,
// dan detailnya dikirim DIAM-DIAM ke `POST /internalLog/client`. Tamu cukup menyebut kodenya; kita
// cari angka yang sama di kotak "Reference Code" halaman SuperAdmin → Internal Log — tak perlu lagi
// menebak-nebak "gagal menyimpan data tamu" itu gagal di mana.
//
// Kenapa fetch, bukan instance axios app: laporan error tak boleh lewat interceptor yang sedang
// menangani error (rekursi), dan `keepalive` membuat laporan tetap terkirim walau tamu langsung
// menutup tab. Semua jalur di sini menelan error-nya sendiri — pelaporan tak boleh punya suara.

const SENSITIVE = /pass|token|secret|otp|pin|identitynumber|cvv|card/i;
const MAX_STR = 300;
const MAX_BODY = 20000;

// ponytail: 30 laporan per sesi halaman. Cukup untuk satu kunjungan yang bermasalah, dan menahan
// loop retry membanjiri tabel log. Naikkan/pindah ke setting kalau ternyata ada kasus yang terpotong.
const MAX_REPORTS_PER_SESSION = 30;
let sent = 0;

// Angka saja, 6 digit — tamu menyebutkannya lewat telepon/WA, dan huruf-angka campur ("EK2M9XZ4F")
// selalu berakhir "ini huruf O atau angka nol?". Bukan penanda unik global: pencariannya selalu
// berpasangan dengan tanggal di halaman External Log.
// ponytail: 900rb kemungkinan, tabrakan mungkin tapi tak apa (dua error beda di menit yang sama
// masih beda barisnya). Kalau volumenya naik sampai mengganggu, panjangkan jadi 8 digit.
export function newErrorRef(rnd = Math.random()) {
    return String(100000 + Math.floor(rnd * 900000));
}

// Nilai sensitif tak pernah ikut terkirim; string panjang & array besar dipotong supaya satu
// laporan tak pernah membengkak (payload booking bisa memuat foto KTP base64).
export function redact(value, depth = 0) {
    if (value === null || value === undefined) return null;
    if (typeof value === "string") return value.length > MAX_STR ? `${value.slice(0, MAX_STR)}…` : value;
    if (typeof value !== "object") return value;
    if (depth >= 4) return "[deep]";
    if (Array.isArray(value)) return value.slice(0, 20).map((v) => redact(v, depth + 1));
    const out = {};
    for (const [key, val] of Object.entries(value)) {
        out[key] = SENSITIVE.test(key) ? "***" : redact(val, depth + 1);
    }
    return out;
}

export function buildAxiosReport(error, ref) {
    const config = error?.config || {};
    const response = error?.response;
    let payload = config.data;
    if (typeof payload === "string") {
        try { payload = JSON.parse(payload); } catch { /* bukan JSON, biarkan apa adanya */ }
    }
    return {
        Ref: ref,
        Kind: "api",
        Method: String(config.method || "").toUpperCase(),
        Url: String(config.url || ""),
        Status: response?.status ?? 0, // 0 = tak ada respons sama sekali (jaringan putus / timeout)
        Code: response?.data?.code ?? null,
        Msg: String(response?.data?.msg ?? error?.message ?? "").slice(0, MAX_STR),
        Payload: redact(payload),
        Response: redact(response?.data),
    };
}

// Toast tak tahu error mana yang memicunya, jadi kode diambil dari "error terakhir, sekali pakai".
// ponytail: jendela 6 detik + sekali pakai. Kalau nanti ada toast yang WAJIB tepat kodenya,
// oper ref-nya eksplisit dari pemanggil, jangan lebarkan jendela ini.
let recent = null;
export function rememberRef(ref, at = Date.now()) {
    recent = { ref, at };
}
export function takeRecentRef(now = Date.now(), maxAgeMs = 6000) {
    if (!recent || now - recent.at > maxAgeMs) return null;
    const { ref } = recent;
    recent = null;
    return ref;
}

// Konteks yang hanya diketahui app (mis. UserId dari localStorage terenkripsi). Dipasang sekali
// lewat setter supaya berkas ini tetap murni — ia diuji dengan `node`, di mana alias `@/` dan
// crypto-js tak ada. Fungsinya dipanggil saat error terjadi, jadi user yang baru login pun ikut.
let readContext = () => ({});
export function setLogContext(fn) {
    readContext = typeof fn === "function" ? fn : () => ({});
}

export function sendErrorLog(report, baseUrl) {
    try {
        if (!baseUrl || typeof fetch !== "function") return;
        if (sent >= MAX_REPORTS_PER_SESSION) return;
        sent += 1;
        let context = {};
        try { context = readContext() || {}; } catch { /* konteks opsional, jangan gagalkan laporan */ }
        const full = {
            ...report,
            ...context,
            Page: String(window.location.href).slice(0, MAX_STR),
            // Id perangkat tamu — penanda satu-satunya saat ia belum login.
            Visitor: localStorage.getItem("fingerprintId") || null,
            At: new Date().toISOString(),
        };
        let body = JSON.stringify(full);
        // Jangan potong string JSON-nya (jadi tak bisa di-parse) — buang isi beratnya lalu rakit ulang.
        if (body.length > MAX_BODY) {
            body = JSON.stringify({ ...full, Payload: "[too large]", Response: "[too large]" });
        }
        fetch(`${String(baseUrl).replace(/\/+$/, "")}/internalLog/client`, {
            method: "POST",
            keepalive: true,
            headers: { "Content-Type": "application/json" },
            body,
        }).catch(() => {});
    } catch { /* diam: pelaporan error tak boleh melahirkan error baru */ }
}

export function reportAxiosError(error, baseUrl) {
    try {
        if (!error || error.__chLogged) return null;
        error.__chLogged = true; // dibaca juga oleh handler unhandledrejection supaya tak dobel
        const ref = newErrorRef();
        rememberRef(ref);
        sendErrorLog(buildAxiosReport(error, ref), baseUrl);
        return ref;
    } catch { return null; }
}

export function installGlobalErrorLog(baseUrl) {
    if (typeof window === "undefined" || window.__chErrorLogInstalled) return;
    window.__chErrorLogInstalled = true;

    window.addEventListener("error", (event) => {
        if (!event?.message) return; // gambar/script gagal muat juga memicu "error", tapi tanpa message
        const ref = newErrorRef();
        rememberRef(ref);
        sendErrorLog({
            Ref: ref,
            Kind: "js",
            Status: 0,
            Msg: String(event.message).slice(0, MAX_STR),
            Url: `${event.filename || ""}:${event.lineno || 0}`,
        }, baseUrl);
    });

    window.addEventListener("unhandledrejection", (event) => {
        const reason = event?.reason;
        if (reason?.__chLogged) return; // error axios sudah dicatat interceptor
        const ref = newErrorRef();
        rememberRef(ref);
        sendErrorLog({
            Ref: ref,
            Kind: "promise",
            Status: 0,
            Msg: String(reason?.message || reason || "").slice(0, MAX_STR),
            Url: "",
        }, baseUrl);
    });
}
